import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { captureException } from '@sentry/gatsby';
import { Column, Row, TextInput, Select, Button, Icon } from '@pella/sash-framework';
import { usePostalCode } from '../../../../../hooks/use-postal-code';
import { getContractors } from '../../../../../actions/location-actions';
import { isValidPostal } from '../../../../utilities/validations';
import LoadingOverlay from '../../../loading-overlay';
import { useLocation } from '../../../../../context/LocationContext';

const PccZipForm = () => {
	const dispatch = useDispatch();
	const locations = useSelector((state) => state.locations);
	const userPostal = usePostalCode();
	const { setLocation } = useLocation();
	const [values, setValues] = useState({ pccZip: '', pccMiles: 50 });
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => setValues((current) => ({ ...current, pccZip: userPostal })), [userPostal]);

	useEffect(() => {
		if (!locations.platinum && !locations.certified && isValidPostal(userPostal)) {
			dispatch(getContractors(userPostal, values.pccMiles));
		}
	}, [locations.platinum, locations.certified, userPostal, values.pccMiles, dispatch]);

	const onFormSubmit = async () => {
		setLoading(true);
		setError(null);

		if (isValidPostal(values.pccZip)) {
			try {
				await setLocation(values.pccZip);
				dispatch(getContractors(values.pccZip, values.pccMiles));
			} catch (err) {
				captureException(err);
			}
		} else {
			setError('Please enter a valid zip code');
		}

		setLoading(false);
	};

	const onInputChange = (event) => setValues({ ...values, [event.target.name]: event.target.value });

	return (
		<form
			autoComplete='off'
			onSubmit={(event) => {
				event.preventDefault();
				onFormSubmit();
			}}
			data-testid='pcc-zip-form'
		>
			{loading && <LoadingOverlay />}

			<Row
				align='end'
				className='gap-x-1'
				noGutters
			>
				<Column xs='fluid'>
					<TextInput
						id='pccZip'
						placeholder='Enter Zip Code'
						labelText='Zip Code'
						onChange={onInputChange}
						value={values.pccZip}
						groupClass='!mb-0'
						autoComplete='postal-code'
					/>
				</Column>

				<Column xs='fluid'>
					<Select
						id='pccMiles'
						className='rounded-none'
						groupClass='!mb-0'
						theme='redesign'
						labelText='Within'
						onChange={onInputChange}
						defaultValue={values.pccMiles}
						autoComplete='off'
					>
						<option value={10}>10 miles</option>
						<option value={25}>25 miles</option>
						<option value={50}>50 miles</option>
						<option value={75}>75 miles</option>
						<option value={100}>100 miles</option>
					</Select>
				</Column>

				<Column xs='auto'>
					<Button
						type='submit'
						color='primary-alt'
						className='h-[54px] px-4 py-0'
					>
						<span className='sr-only'>Search</span>
						<Icon
							name='search'
							className='mx-auto block'
						/>
					</Button>
				</Column>
			</Row>

			{error && <p className='text-body-2 mt-2.5 text-red'>{error}</p>}
		</form>
	);
};

export default PccZipForm;
